import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { 
    FaRegUser,
    FaUserTie, 
    FaBuilding, 
    FaMapMarkerAlt,
    FaUserShield,
    FaShoppingCart,
    FaCreditCard,
    FaUsers,
    FaMoneyBillWave
} from "react-icons/fa";
import { get, getWithParams } from "../../utils/fetchAPI";
import PageTitle from "../../components/page-title/pageTitle";
import { RiHome3Line } from "react-icons/ri";
import Loader from "../../components/loader/loder";
import StatCard from "../../components/cards/statCard";
import ActivityDonutChart from "./ui/ActivityDonutChart";
import ActiveDoctorsChart from "./ui/ActiveDoctorsChart";
import DoctorStatsBarChart from "./ui/DoctorStatsBarChart";

// Skeleton loader component for stat cards
const StatCardSkeleton = () => (
    <div className="text-black flex items-end justify-between p-5 rounded-md drop-shadow-[0_0px_20px_rgba(203,229,255,0.5)] bg-white animate-pulse">
        <div className="flex flex-col gap-3.5 w-full">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-8 bg-gray-300 rounded w-1/2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
        </div>
        <div className="flex items-center justify-center p-3 rounded-full bg-gray-200 w-12 h-12"></div>
    </div>
);

const Dashboard = () => {
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
    
    // Basic statistics
    const [dailyActiveDoctors, setDailyActiveDoctors] = useState(0);
    const [totalActiveDoctors, setTotalActiveDoctors] = useState(0);
    const [totalInactiveDoctors, setTotalInactiveDoctors] = useState(0);
    const [totalStaffs, setTotalStaffs] = useState(0);
    const [totalOrganizations, setTotalOrganizations] = useState(0);
    const [totalLocations, setTotalLocations] = useState(0);
    const [totalAdminUsers, setTotalAdminUsers] = useState(0);
    
    // Statistics with date filters
    const [totalMedicines, setTotalMedicines] = useState(0);
    const [totalLabTests, setTotalLabTests] = useState(0);
    const [totalAppointments, setTotalAppointments] = useState(0);
    const [totalSMS, setTotalSMS] = useState(0);
    
    // Order statistics with date filters
    const [totalOrders, setTotalOrders] = useState(0);
    const [totalPaidOrders, setTotalPaidOrders] = useState(0);
    const [totalPaidUsers, setTotalPaidUsers] = useState(0);
    const [totalPaidAmount, setTotalPaidAmount] = useState(0);
    const [orderStatsLoading, setOrderStatsLoading] = useState(false);
    
    const [activeDoctorsData, setActiveDoctorsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    
    // Get current month dates for default date range
    const getCurrentMonthDates = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return {
            startDate: firstDay.toISOString().split('T')[0],
            endDate: lastDay.toISOString().split('T')[0]
        };
    };

    const [dateRange, setDateRange] = useState(getCurrentMonthDates());

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [
                    dailyDoctorsResponse,
                    activeDoctorsResponse,
                    inactiveDoctorsResponse,
                    staffsResponse,
                    organizationsResponse,
                    locationsResponse,
                    adminUsersResponse
                ] = await Promise.all([
                    get("dashboard/daily-active-doctors"),
                    get("dashboard/total-active-doctors"),
                    get("dashboard/total-inactive-doctors"),
                    get("dashboard/total-staffs"),
                    get("dashboard/total-organizations"),
                    get("dashboard/total-locations"),
                    get("dashboard/total-admin-users")
                ]);

                setDailyActiveDoctors(dailyDoctorsResponse.data?.activeDoctorsCount || 0);
                setTotalActiveDoctors(activeDoctorsResponse.data?.totalActiveDoctors || 0);
                setTotalInactiveDoctors(inactiveDoctorsResponse.data?.totalInactiveDoctors || 0);
                setTotalStaffs(staffsResponse.data?.totalStaffs || 0);
                setTotalOrganizations(organizationsResponse.data?.totalOrganizations || 0);
                setTotalLocations(locationsResponse.data?.totalLocations || 0);
                setTotalAdminUsers(adminUsersResponse.data?.totalAdminUsers || 0);
            } catch (error) {
                console.error("Error fetching basic statistics:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Fetch statistics with date filters (only when Get Data button is clicked)
    const fetchDateFilteredStats = async () => {
        setStatsLoading(true);
        try {
            const currentParams = dateRange;

            const [
                currMed,
                currLab,
                currApp,
                currSms,
            ] = await Promise.all([
                getWithParams("dashboard/total-medicines-in-prescription", currentParams),
                getWithParams("dashboard/total-labtests-in-prescription", currentParams),
                getWithParams("dashboard/total-appointments", currentParams),
                getWithParams("dashboard/total-sms-sent", currentParams),
            ]);

            setTotalMedicines(currMed.data?.totalMedicinesInPrescription || 0);
            setTotalLabTests(currLab.data?.totalLabTestsInPrescription || 0);
            setTotalAppointments(currApp.data?.totalAppointments || 0);
            setTotalSMS(currSms.data?.totalSmsSent || 0);

        } catch (e) {
            console.error(e);
        } finally {
            setStatsLoading(false);
        }
    };


    // Fetch active doctors chart data (only when Get Data button is clicked)
    const fetchActiveDoctorsByDate = async () => {
        setChartLoading(true);
        try {
            const response = await getWithParams("dashboard/active-doctors-by-date", {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            });

            console.log("Active Doctors By Date:", response);
            setActiveDoctorsData(response.data);
        } catch (error) {
            console.error("Error fetching active doctors by date:", error);
        } finally {
            setChartLoading(false);
        }
    };


    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Fetch order statistics with date filter (only when Get Data button is clicked)
    const fetchOrderStats = async () => {
        setOrderStatsLoading(true);
        try {
            const params = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            };

            const [
                ordersResponse,
                paidOrdersResponse,
                paidUsersResponse,
                paidAmountResponse
            ] = await Promise.all([
                getWithParams("dashboard/total-orders", params),
                getWithParams("dashboard/total-paid-orders", params),
                getWithParams("dashboard/paid-users", params),
                getWithParams("dashboard/total-paid-amount", params)
            ]);

            setTotalOrders(ordersResponse.data?.totalOrders || 0);
            setTotalPaidOrders(paidOrdersResponse.data?.totalPaidOrders || 0);
            setTotalPaidUsers(paidUsersResponse.data?.totalPaidUsers || 0);
            setTotalPaidAmount(paidAmountResponse.data?.totalPaidAmount || 0);
        } catch (error) {
            console.error("Error fetching order statistics:", error);
        } finally {
            setOrderStatsLoading(false);
        }
    };


    if (!isLoggedIn) {
        return <Navigate to="/login" />;
    }

    return (
        <div className="flex flex-col gap-5 w-full h-full px-2 sm:px-0">
            {loading ? (
                <div className="flex justify-center items-center h-full">
                    <Loader />
                </div>
            ) : (
                <>
                    <PageTitle icon={<RiHome3Line />} title="Dashboard" />
                    <div className="flex flex-col gap-6 w-full">
                        <div className="grid grid-cols-1 lg:grid-cols-4 grid-rows-2 gap-3.5 lg:auto-rows-fr">
                            {/* Doctor Statistics Section */}
                            <div className="items-center bg-white rounded-lg drop-shadow-[0_0px_20px_rgba(203,229,255,0.5)] col-span-2 row-span-2 h-auto">
                                <DoctorStatsBarChart
                                    dailyActiveDoctors={dailyActiveDoctors}
                                    totalActiveDoctors={totalActiveDoctors}
                                    totalInactiveDoctors={totalInactiveDoctors}
                                    loading={loading}
                                />
                            </div>

                            {/* User Statistics Section */}
                            <div className="col-span-2 row-span-1 bg-white p-5 rounded-md flex flex-col gap-3">
                                <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                                    <FaRegUser className="text-primary" />
                                    User Statistics
                                </h2>
                                {loading ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                        <StatCardSkeleton />
                                        <StatCardSkeleton />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <StatCard
                                            title="Total Staff Users"
                                            count={totalStaffs}
                                            linkText="View staff list"
                                linkTo="/user-create"
                                            icon={<FaUserTie className="w-5 h-5 text-purple-500" />}
                                            bgColor="bg-purple-100"
                                        />
                                        <StatCard
                                            title="Total Admin Users"
                                            count={totalAdminUsers}
                                            linkText="View admin list"
                                            linkTo="/admin-list"
                                            icon={<FaUserShield className="w-5 h-5 text-red-500" />}
                                            bgColor="bg-red-100"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Organization & Location Statistics Section */}
                            <div className="col-span-2 row-span-1 bg-white p-5 rounded-md flex flex-col gap-3">
                                <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                                    <FaBuilding className="text-primary" />
                                    Organization & Location Statistics
                                </h2>
                                {loading ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <StatCardSkeleton />
                                        <StatCardSkeleton />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <StatCard
                                            title="Total Organizations"
                                            count={totalOrganizations}
                                            linkText="View organizations"
                                            linkTo="/organization-list"
                                            icon={<FaBuilding className="w-5 h-5 text-teal-500" />}
                                bgColor="bg-teal-100"
                            />
                            <StatCard
                                            title="Total Locations"
                                            count={totalLocations}
                                            linkText="View locations"
                                            linkTo="/location-list"
                                            icon={<FaMapMarkerAlt className="w-5 h-5 text-orange-500" />}
                                            bgColor="bg-orange-100"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>


                        <div className="p-5 bg-white flex flex-col gap-6 rounded-lg">

                            {/* Unified Date Filter Section */}
                            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg p-4 border-2 border-teal-200">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                    <h2 className="text-lg font-semibold text-gray-700">
                                        Date Range Filter (For All Statistics Below)
                                    </h2>
                                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm font-medium text-gray-700">From:</label>
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={dateRange.startDate}
                                                onChange={handleDateChange}
                                                className="border-2 border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm font-medium text-gray-700">To:</label>
                                            <input
                                                type="date"
                                                name="endDate"
                                                value={dateRange.endDate}
                                                onChange={handleDateChange}
                                                className="border-2 border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                            />
                                        </div>
                                        <button
                                            onClick={() => {
                                                fetchDateFilteredStats();
                                                fetchOrderStats();
                                                fetchActiveDoctorsByDate();
                                            }}
                                            disabled={statsLoading || orderStatsLoading || chartLoading}
                                            className="px-4 py-1.5 bg-primary text-white rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {(statsLoading || orderStatsLoading || chartLoading) ? "Loading..." : "Get Data"}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <ActivityDonutChart
                                totalMedicines={totalMedicines}
                                totalLabTests={totalLabTests}
                                totalAppointments={totalAppointments}
                                totalSMS={totalSMS}
                                loading={statsLoading}
                            />


                            {/* Order Status Section */}
                            <div className="flex flex-col gap-3">
                                <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                                    <FaShoppingCart className="text-primary" />
                                    Order Status
                                </h2>
                                {orderStatsLoading ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <StatCardSkeleton />
                                        <StatCardSkeleton />
                                        <StatCardSkeleton />
                                        <StatCardSkeleton />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <StatCard
                                            title="Total Orders"
                                            count={totalOrders}
                                            linkText="View orders"
                                            linkTo="/order-list"
                                            icon={<FaShoppingCart className="w-5 h-5 text-indigo-500" />}
                                            bgColor="bg-indigo-100"
                                        />
                                        <StatCard
                                            title="Total Paid Orders"
                                            count={totalPaidOrders}
                                            linkText="View paid orders"
                                            linkTo="/order-list"
                                            icon={<FaCreditCard className="w-5 h-5 text-green-500" />}
                                            bgColor="bg-green-100"
                                        />
                                        <StatCard
                                            title="Paid Users"
                                            count={totalPaidUsers}
                                            linkText="View users"
                                            linkTo="/doctor-list"
                                            icon={<FaUsers className="w-5 h-5 text-blue-500" />}
                                            bgColor="bg-blue-100"
                                        />
                                        <StatCard
                                            title="Total Paid Amount"
                                            count={totalPaidAmount > 0 ? new Intl.NumberFormat('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            }).format(totalPaidAmount) + ' Tk' : '0.00 Tk'}
                                            linkText="View orders"
                                            linkTo="/order-list"
                                            icon={<FaMoneyBillWave className="w-5 h-5 text-emerald-500" />}
                                            bgColor="bg-emerald-100"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Active Doctors Chart with Date Range */}
                            <ActiveDoctorsChart
                                chartLoading={chartLoading}
                                activeDoctorsData={activeDoctorsData}
                            />
                        </div>

                    </div>
                </>
            )}
        </div>
    );
};

export default Dashboard;
